<template>
  <registry-page :title="resolvedTitle" :description="description">
    <template #metadata>
      Период: {{ periodLabel }}
    </template>
    <template #actions>
      <q-btn
        v-if="canEditFinance && !readOnlyRegistry && singleCreateOption"
        color="primary"
        no-caps
        icon="add"
        :label="createLabel"
        :to="createRoute(singleCreateOption)"
        data-testid="registry-primary-create"
      />
      <q-btn-dropdown
        v-else-if="canEditFinance && !readOnlyRegistry"
        color="primary"
        no-caps
        icon="add"
        label="Создать"
        :data-testid="isPaymentRegistry ? 'finance-create-payment' : 'finance-create-document'"
      >
        <q-list v-if="!isPaymentRegistry">
          <q-item
            v-for="option in createOptions"
            :key="option.key"
            v-close-popup
            clickable
            :to="createRoute(option)"
          >
            <q-item-section>{{ option.label }}</q-item-section>
          </q-item>
        </q-list>
        <q-list v-else>
          <q-item v-close-popup clickable :to="createRoute(paymentCreateOption)">
            <q-item-section>Поручение вручную</q-item-section>
          </q-item>
          <q-item v-close-popup clickable @click="openUploadDialog">
            <q-item-section>Поручение из файла</q-item-section>
          </q-item>
        </q-list>
      </q-btn-dropdown>
    </template>

    <q-banner v-if="unsupportedMessage" rounded class="bg-warning text-dark q-mb-md" role="alert">
      {{ unsupportedMessage }}
    </q-banner>
    <q-banner v-if="exportError" rounded class="bg-negative text-white q-mb-md" role="alert">
      {{ exportError }}
    </q-banner>

    <app-data-table
      :title="tableTitle || (isPaymentRegistry ? 'Платежные поручения' : 'Документы')"
      :count="visibleRows.length"
      :rows="visibleRows"
      :columns="columns"
      :pagination="pagination"
      :state="tableState"
      :state-title="stateTitle"
      :state-message="stateMessage"
      :storage-key="isPaymentRegistry ? 'payment-documents' : 'finance-documents'"
      :selection="isPaymentRegistry ? 'multiple' : 'none'"
      :selected="selected"
      :loading="loading"
      :data-testid="isPaymentRegistry ? 'payments-table' : 'documents-table'"
      @update:selected="selected = $event"
      @row-click="onRowClick"
      @retry="loadDocuments"
      @reset="resetFilters"
    >
      <template #filters>
        <filter-bar
          ref="filterBar"
          v-model="filters.search"
          :search-label="searchLabel"
          search-test-id="document-search"
          :has-filters="hasFilters"
          :applied-filters="appliedFilters"
          @search="syncSearch"
          @reset="resetFilters"
          @remove="removeFilter"
        >
          <template #quick>
            <q-select
              v-model="filters.direction"
              data-testid="document-direction"
              dense outlined emit-value map-options
              label="Направление"
              :options="directionOptions"
              @update:model-value="applyServerFilters"
            />
            <q-select
              v-if="!fixedDocumentType"
              v-model="filters.type"
              data-testid="document-type"
              dense outlined emit-value map-options
              label="Тип документа"
              :options="typeOptions"
              @update:model-value="onTypeChange"
            />
            <date-range-filter v-model="filters.period" label="Период" @apply="applyPeriodFilters" @reset="resetPeriod" />
          </template>
          <template #advanced>
            <div class="document-registry__advanced">
              <q-select
                v-model="filters.direction"
                data-testid="document-direction-advanced"
                dense outlined emit-value map-options
                label="Направление"
                :options="directionOptions"
              />
              <q-select
                v-if="!fixedDocumentType"
                v-model="filters.type"
                data-testid="document-type-advanced"
                dense outlined emit-value map-options
                label="Тип документа"
                :options="typeOptions"
              />
              <q-select
                v-model="filters.status"
                data-testid="document-status"
                dense outlined emit-value map-options
                label="Состояние"
                :options="statusOptions"
              />
              <q-select
                v-if="showVatFilter"
                v-model="filters.vat"
                data-testid="document-vat"
                dense outlined emit-value map-options clearable
                label="Период НДС"
                :options="vatOptions"
              />
              <q-select
                v-if="isPaymentRegistry"
                v-model="filters.account"
                data-testid="document-account"
                dense outlined emit-value map-options clearable
                label="Расчетный счет"
                :options="accountOptions"
              />
              <date-range-filter v-model="filters.period" label="Период" @reset="resetPeriod" />
              <q-btn color="primary" no-caps label="Применить фильтры" @click="applyAdvancedFilters" />
            </div>
          </template>
        </filter-bar>
      </template>

      <template #actions>
        <q-btn
          v-if="isPaymentRegistry && selected.length"
          no-caps
          color="primary"
          icon="download"
          label="Выгрузить выбранные"
          data-testid="payments-export-selected"
          :loading="exportPending"
          :disable="exportPending"
          @click="exportSelected"
        />
      </template>

      <template #body-cell-name="props">
        <q-td :props="props">
          <router-link
            v-if="infoRoute(props.row)"
            :to="infoRoute(props.row)"
            class="document-registry__link"
            @click.stop
          >
            {{ props.row.name }}
          </router-link>
          <button v-else type="button" class="document-registry__unsupported" @click.stop="showUnsupported(props.row)">
            {{ props.row.name }}
          </button>
        </q-td>
      </template>
      <template #body-cell-type="props">
        <q-td :props="props">
          <status-badge
            :label="props.row.type_display"
            :tone="infoRoute(props.row) ? 'info' : 'warning'"
            :icon="infoRoute(props.row) ? 'description' : 'warning'"
          />
        </q-td>
      </template>
      <template #body-cell-status="props">
        <q-td :props="props">
          <status-badge
            :label="props.row.is_active ? 'Активен' : 'Удален'"
            :tone="props.row.is_active ? 'success' : 'danger'"
            :icon="props.row.is_active ? 'check_circle' : 'delete_outline'"
          />
        </q-td>
      </template>
      <template #body-cell-amount="props">
        <q-td :props="props"><amount-cell :value="props.value" /></q-td>
      </template>
      <template #body-cell-vat="props">
        <q-td :props="props">{{ props.row.nds_period_normalized.label }}</q-td>
      </template>
    </app-data-table>

    <q-dialog v-if="isPaymentRegistry && canEditFinance" v-model="uploadOpen" persistent>
      <q-card class="document-registry__upload">
        <q-card-section>
          <h2 class="document-registry__dialog-title">Загрузить файл клиент-банка</h2>
          <p class="text-body2 text-grey-7">Поддерживается существующий формат обмена 1С Клиент-Банк.</p>
        </q-card-section>
        <q-form @submit.prevent="submitPaymentFile">
          <q-card-section>
            <q-file v-model="file" outlined bottom-slots label="Файл клиент-банка" name="file" counter>
              <template #prepend><q-icon name="cloud_upload" /></template>
              <template #append><q-icon v-if="file" name="close" class="cursor-pointer" role="button" tabindex="0" aria-label="Очистить выбранный файл" @click.stop.prevent="file = null" @keydown.enter.prevent="$event.currentTarget.click()" @keydown.space.prevent="$event.currentTarget.click()" /></template>
            </q-file>
            <p v-if="uploadError" class="document-registry__error" role="alert">{{ uploadError }}</p>
          </q-card-section>
          <q-card-actions align="right">
            <q-btn flat no-caps label="Отмена" :disable="uploadPending" v-close-popup />
            <q-btn color="primary" no-caps label="Загрузить" type="submit" :loading="uploadPending" :disable="uploadPending" />
          </q-card-actions>
        </q-form>
      </q-card>
    </q-dialog>
  </registry-page>
</template>

<script>
import axios from 'axios'
import AmountCell from '../../../components/ui/AmountCell.vue'
import AppDataTable from '../../../components/ui/AppDataTable.vue'
import DateRangeFilter from '../../../components/ui/DateRangeFilter.vue'
import FilterBar from '../../../components/ui/FilterBar.vue'
import RegistryPage from '../../../components/ui/RegistryPage.vue'
import StatusBadge from '../../../components/ui/StatusBadge.vue'
import { apiUrl } from '../../../config'
import { unixEndOfDay, unixStartOfDay } from '../../../services/dateFormat'
import {
  DOCUMENT_CREATE_OPTIONS,
  DOCUMENT_DIRECTION_OPTIONS,
  DOCUMENT_STATUS_OPTIONS,
  DOCUMENT_TYPE_OPTIONS,
  defaultDocumentPeriod,
  directionLabel,
  documentFilterQuery,
  documentInfoRoute,
  documentListType,
  documentServerFilterSignature,
  documentTypeLabel,
  normalizeDocumentList,
  normalizeDocumentRegistryFilters
} from '../../../services/documentRegistry'
import { downloadPaymentExport, paymentExportErrorMessage } from '../../../services/paymentExport'

const columns = Object.freeze([
  { name: 'in_org_number', align: 'left', label: 'Номер', field: 'in_org_number', sortable: true, required: true },
  { name: 'name', align: 'left', label: 'Название', field: 'name', sortable: true, required: true },
  { name: 'type', align: 'left', label: 'Тип', field: 'type_display' },
  { name: 'status', align: 'left', label: 'Статус', field: 'is_active' },
  { name: 'direction', align: 'left', label: 'Направление', field: 'direction_display' },
  { name: 'date', align: 'left', label: 'Дата', field: 'date_display', sortable: true, required: true },
  { name: 'vat', align: 'left', label: 'Период НДС', field: (row) => row.nds_period_normalized.label, classes: 'column-priority-secondary' },
  { name: 'amount', align: 'right', label: 'Сумма', field: 'amount', sortable: true },
  { name: 'account', align: 'left', label: 'Расчетный счет', field: (row) => row.account || '—', classes: 'column-priority-secondary' },
  { name: 'user', align: 'left', label: 'Пользователь', field: 'user_display', classes: 'column-priority-secondary' }
])

export default {
  name: 'DocumentRegistryPage',
  components: { AmountCell, AppDataTable, DateRangeFilter, FilterBar, RegistryPage, StatusBadge },
  props: {
    fixedDocumentType: { type: String, default: '' },
    pageTitle: { type: String, default: '' },
    readOnlyRegistry: { type: Boolean, default: false },
    paymentContext: { type: String, default: '' },
    payments: { type: Boolean, default: false },
    singleCreateOption: { type: Object, default: null },
    createLabel: { type: String, default: 'Создать' },
    tableTitle: { type: String, default: '' },
    descriptionText: { type: String, default: '' },
    searchLabel: { type: String, default: 'Номер, название, пользователь или счет' }
  },
  data() {
    const fixedType = this.payments ? 'payment' : (this.fixedDocumentType || null)
    return {
      columns,
      rows: [],
      filters: normalizeDocumentRegistryFilters(this.$route.query, this.$store.state.filter_period, fixedType),
      typeOptions: DOCUMENT_TYPE_OPTIONS,
      directionOptions: DOCUMENT_DIRECTION_OPTIONS,
      statusOptions: DOCUMENT_STATUS_OPTIONS,
      createOptions: DOCUMENT_CREATE_OPTIONS,
      paymentCreateOption: DOCUMENT_CREATE_OPTIONS.find((item) => item.key === 'payment'),
      pagination: { sortBy: 'date', descending: true, page: 1, rowsPerPage: 50 },
      accounts: [],
      selected: [],
      loading: true,
      error: '',
      errorState: 'error',
      unsupportedMessage: '',
      exportPending: false,
      exportRequestId: null,
      exportError: '',
      uploadOpen: false,
      uploadPending: false,
      uploadError: '',
      file: null,
      handlers: [],
      lastRequestedSignature: ''
    }
  },
  computed: {
    isPaymentRegistry() {
      return this.payments || this.fixedDocumentType === 'payment'
    },
    resolvedFixedType() {
      return this.isPaymentRegistry ? 'payment' : (this.fixedDocumentType || null)
    },
    resolvedTitle() {
      if (this.pageTitle) return this.pageTitle
      if (this.isPaymentRegistry) return 'Платежные поручения'
      if (this.resolvedFixedType) return documentTypeLabel(this.resolvedFixedType)
      return 'Документы'
    },
    description() {
      if (this.descriptionText) return this.descriptionText
      if (this.isPaymentRegistry) return 'Реестр платежных поручений с выбором и выгрузкой в клиент-банк.'
      if (this.resolvedFixedType === 'bill_to_pay') return 'Счета бухгалтерского и биллингового контуров. Биллинговые счета доступны только для просмотра.'
      if (this.readOnlyRegistry) return 'Документы доступны только для просмотра.'
      return 'Единый реестр бухгалтерских документов организации.'
    },
    canEditFinance() {
      return this.$store.state.permissions.edit_fin_documents === true
    },
    periodLabel() {
      return `${this.filters.period.from} — ${this.filters.period.to}`
    },
    showVatFilter() {
      const type = this.resolvedFixedType || this.filters.type
      return !['payment', 'bill_to_pay'].includes(type)
    },
    vatOptions() {
      const values = new Map()
      this.rows.forEach((row) => {
        const period = row.nds_period_normalized
        if (period.value) values.set(period.value, { value: period.value, label: period.label })
      })
      return [...values.values()]
    },
    accountOptions() {
      return this.accounts.map((account) => ({
        value: String(account.account_number || ''),
        label: `${account.bank_name || 'Банк'} (…${String(account.account_number || '').slice(-4)})`
      }))
    },
    visibleRows() {
      const search = this.filters.search.trim().toLocaleLowerCase('ru-RU')
      return this.rows.filter((row) => {
        const matchesSearch = !search || [
          row.in_org_number, row.name, row.type_display, row.direction_display, row.user_display, row.account
        ].some((value) => String(value || '').toLocaleLowerCase('ru-RU').includes(search))
        const matchesVat = !this.filters.vat || row.nds_period_normalized.value === this.filters.vat
        return matchesSearch && matchesVat
      })
    },
    hasFilters() {
      const defaults = defaultDocumentPeriod(this.$store.state.filter_period)
      return Boolean(
        this.filters.search || this.filters.direction !== 'all' ||
        (!this.resolvedFixedType && this.filters.type !== 'all') || this.filters.status !== 'active' ||
        this.filters.period.from !== defaults.from || this.filters.period.to !== defaults.to ||
        this.filters.vat || (this.isPaymentRegistry && this.filters.account)
      )
    },
    appliedFilters() {
      const filters = [{ key: 'period', label: `Период: ${this.periodLabel}` }]
      if (this.resolvedFixedType) filters.push({ key: 'fixed-type', label: `Тип: ${documentTypeLabel(this.resolvedFixedType)}` })
      else if (this.filters.type !== 'all') filters.push({ key: 'type', label: `Тип: ${documentTypeLabel(this.filters.type)}` })
      if (this.filters.search) filters.push({ key: 'search', label: `Поиск: ${this.filters.search}` })
      if (this.filters.direction !== 'all') filters.push({ key: 'direction', label: `Направление: ${directionLabel(this.filters.direction)}` })
      if (this.filters.status !== 'active') filters.push({ key: 'status', label: 'Состояние: удаленные' })
      if (this.filters.vat) filters.push({ key: 'vat', label: `Период НДС: ${this.vatOptions.find((item) => item.value === this.filters.vat)?.label || this.filters.vat}` })
      if (this.isPaymentRegistry && this.filters.account) filters.push({ key: 'account', label: `Счет: …${this.filters.account.slice(-4)}` })
      return filters
    },
    tableState() {
      if (this.loading) return 'loading'
      if (this.error) return this.errorState
      if (!this.rows.length) return this.hasFilters ? 'filtered-empty' : 'empty'
      if (!this.visibleRows.length) return 'filtered-empty'
      return 'ready'
    },
    stateTitle() {
      if (this.error) return 'Не удалось загрузить документы'
      return this.hasFilters ? 'Документы не найдены' : 'Документов пока нет'
    },
    stateMessage() {
      return this.error || (this.hasFilters ? 'Измените или сбросьте фильтры.' : 'За выбранный период документов нет.')
    }
  },
  watch: {
    '$route.fullPath'() {
      this.filters = normalizeDocumentRegistryFilters(
        this.$route.query,
        this.$store.state.filter_period,
        this.resolvedFixedType
      )
      this.selected = []
      this.unsupportedMessage = ''
      const signature = documentServerFilterSignature(this.filters, this.resolvedFixedType, this.isPaymentRegistry)
      if (this.connection.isAuthenticated && signature !== this.lastRequestedSignature) this.loadDocuments()
    }
  },
  mounted() {
    this.listen('didReceiveDocumentsList', this.onDocuments)
    this.listen('onGetOrgAccountsList', this.onAccounts)
    this.listen('onCreateTxtFromPayement', this.onPaymentExport)
    this.listen('connectionReady', this.loadWorkspace)
    if (this.connection.isAuthenticated) this.loadWorkspace()
  },
  beforeUnmount() {
    this.handlers.forEach(([event, handler]) => this.emitter.off(event, handler))
  },
  methods: {
    listen(event, handler) {
      this.emitter.on(event, handler)
      this.handlers.push([event, handler])
    },
    loadWorkspace() {
      this.loadDocuments()
      if (this.isPaymentRegistry) this.connection.getOrgAccountsList('document-registry-accounts')
    },
    loadDocuments() {
      const signature = documentServerFilterSignature(this.filters, this.resolvedFixedType, this.isPaymentRegistry)
      this.lastRequestedSignature = signature
      this.loading = true
      this.error = ''
      this.connection.getDocumentsList(
        'document-registry-list',
        unixStartOfDay(this.filters.period.from),
        unixEndOfDay(this.filters.period.to),
        this.filters.direction,
        documentListType(this.filters, this.resolvedFixedType),
        this.filters.status !== 'deleted',
        null,
        null,
        this.isPaymentRegistry ? (this.filters.account || null) : null
      )
    },
    onDocuments(response) {
      this.loading = false
      const normalized = normalizeDocumentList(response)
      if (!normalized.ok) {
        this.errorState = normalized.state || 'error'
        this.error = normalized.message
        return
      }
      this.rows = normalized.rows
      this.selected = []
    },
    onAccounts(response) {
      if (response.type === 'error' || response.error || !Array.isArray(response.data)) return
      this.accounts = response.data
    },
    infoRoute(row) {
      return documentInfoRoute(row, this.paymentContext)
    },
    createRoute(option) {
      return {
        name: option.routeName,
        ...(option.key === 'payment' && this.paymentContext ? { query: { context: this.paymentContext } } : {})
      }
    },
    onRowClick(event, row) {
      if (event?.target?.closest('.q-checkbox, a, button')) return
      const route = this.infoRoute(row)
      if (route) this.$router.push(route)
      else this.showUnsupported(row)
    },
    showUnsupported(row) {
      this.unsupportedMessage = `Тип «${row.document_type || 'не указан'}» пока не поддерживает карточку просмотра. Документ сохранен в реестре и не был скрыт.`
    },
    onTypeChange() {
      if (!this.showVatFilter) this.filters.vat = ''
      this.applyServerFilters()
    },
    applyServerFilters() {
      this.updateRoute(true)
    },
    async applyPeriodFilters() {
      await this.updateRoute(true)
      await this.$nextTick()
      const triggers = [...this.$el.querySelectorAll('[data-testid="date-range-filter"] > button')]
      const visibleTrigger = triggers.find((trigger) => trigger.offsetParent !== null) || triggers[0]
      if (visibleTrigger) visibleTrigger.focus()
    },
    applyAdvancedFilters() {
      if (!this.showVatFilter) this.filters.vat = ''
      if (this.$refs.filterBar) this.$refs.filterBar.advancedOpen = false
      this.updateRoute(true)
    },
    syncSearch() {
      this.updateRoute(false)
    },
    resetFilters() {
      this.filters = normalizeDocumentRegistryFilters({}, this.$store.state.filter_period, this.resolvedFixedType)
      this.updateRoute(true)
    },
    resetPeriod() {
      this.filters.period = defaultDocumentPeriod(this.$store.state.filter_period)
      this.updateRoute(true)
    },
    removeFilter(key) {
      if (key === 'fixed-type') return
      if (key === 'search') this.filters.search = ''
      if (key === 'direction') this.filters.direction = 'all'
      if (key === 'type') this.filters.type = 'all'
      if (key === 'status') this.filters.status = 'active'
      if (key === 'period') this.filters.period = defaultDocumentPeriod(this.$store.state.filter_period)
      if (key === 'vat') this.filters.vat = ''
      if (key === 'account') this.filters.account = ''
      this.updateRoute(true)
    },
    async updateRoute(push) {
      const query = documentFilterQuery(
        this.filters,
        this.$store.state.filter_period,
        this.resolvedFixedType,
        this.isPaymentRegistry
      )
      if (JSON.stringify(query) === JSON.stringify(this.$route.query)) {
        const signature = documentServerFilterSignature(this.filters, this.resolvedFixedType, this.isPaymentRegistry)
        if (signature !== this.lastRequestedSignature) this.loadDocuments()
        return
      }
      await this.$router[push ? 'push' : 'replace']({ query })
    },
    exportSelected() {
      if (this.exportPending || !this.selected.length) return
      this.exportPending = true
      this.exportError = ''
      this.exportRequestId = this.connection.makeRequestId('document-registry-export')
      this.connection.createTxtFromPayement(this.exportRequestId, this.selected.map((row) => row.id))
    },
    onPaymentExport(response) {
      if (!this.exportPending || !this.exportRequestId || response.request_id !== this.exportRequestId) return
      this.exportRequestId = null
      if (response.type === 'error' || response.error) {
        this.exportPending = false
        this.exportError = paymentExportErrorMessage(response)
        return
      }
      downloadPaymentExport(response)
        .catch(() => { this.exportError = 'Не удалось скачать файл выгрузки платежных поручений' })
        .finally(() => { this.exportPending = false })
    },
    openUploadDialog() {
      this.uploadError = ''
      this.uploadOpen = true
    },
    async submitPaymentFile() {
      if (this.uploadPending) return
      if (!this.file) {
        this.uploadError = 'Выберите файл клиент-банка.'
        return
      }
      this.uploadPending = true
      this.uploadError = ''
      const formData = new FormData()
      formData.append('file', this.file)
      formData.append('user', this.connection.user_id)
      formData.append('org_id', this.connection.selectedOrgId(this.$q))
      formData.append('token', this.connection.token)
      formData.append('token_uuid', this.connection.token_uuid)
      try {
        const response = await axios({
          method: 'post',
          url: `${apiUrl}/upload/payment/`,
          data: formData,
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-Auth-Token': this.connection.token,
            'X-Auth-Token-Uuid': this.connection.token_uuid
          }
        })
        const payload = response.data || {}
        if (payload.error || payload.success === 0 || (payload.errors || []).length) {
          const details = (payload.errors || []).map((item) => item.message || item.error).join('; ')
          this.uploadError = details || payload.message || 'Ошибка при обработке файла.'
          return
        }
        this.file = null
        this.uploadOpen = false
        this.$q.notify({ message: `Импортировано: создано ${payload.created || 0}, обновлено ${payload.updated || 0}`, color: 'positive' })
        this.loadDocuments()
      } catch {
        this.uploadError = 'Ошибка при загрузке файла.'
      } finally {
        this.uploadPending = false
      }
    }
  }
}
</script>

<style scoped>
.document-registry__advanced { display: grid; gap: var(--sid-space-3); }
.document-registry__link { color: var(--sid-color-primary); font-weight: var(--sid-font-weight-medium); }
.document-registry__unsupported { padding: 0; border: 0; background: transparent; color: var(--sid-color-warning); font: inherit; text-align: left; text-decoration: underline; cursor: pointer; }
.document-registry__upload { width: min(30rem, 94vw); }
.document-registry__dialog-title { margin: 0; font-size: var(--sid-font-size-section); }
.document-registry__error { margin: var(--sid-space-2) 0 0; color: var(--sid-color-danger); }
</style>
